import ErrorIcon from '@mui/icons-material/Error'
import {Alert, CircularProgress, inputBaseClasses, Typography} from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import type {AutocompleteRenderInputParams} from '@mui/material/Autocomplete/Autocomplete'
import TextField from '@mui/material/TextField'
import {type HTMLAttributes, memo, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router'
import type {SpotCollection, SpotPartial} from '../../../@types/entity/Spot'
import {useSpotsFetcher} from '../../../hooks/entity/useSpot'
import {useEffectEvent} from '../../../hooks/useEffectEvent'
import {getSpotUrl} from '../../../hooks/useSpotNavigate'
import {AbortError} from '../../../utils/ApiError'
import {asyncDebounce} from '../../../utils/debounce'

interface CustomItem {
  type: string
  name: string
  extra?: string
}

const typeMore = 'more'
const typeError = 'error'

type Status = 'loading' | 'error' | 'valid'

export const SpotSelector = memo(() => {
  const [status, setStatus] = useState<Status | undefined>()
  const running = useRef(false)
  const [fetchError, setError] = useState<string | undefined>()
  const [options, setOptions] = useState<readonly (SpotPartial | CustomItem)[]>([])
  const controllerRef = useRef<AbortController>(undefined)
  const navigate = useNavigate()
  const spotsFetcher = useSpotsFetcher()
  const {t} = useTranslation()

  const fetch = useMemo(() => asyncDebounce(async (search: string): Promise<undefined | SpotCollection> => {
    if (controllerRef.current) {
      controllerRef.current.abort()
    }
    controllerRef.current = new AbortController()

    try {
      const response = await spotsFetcher({name: search}, {
        signal: controllerRef.current.signal,
      })
      controllerRef.current = undefined

      return response
    } catch (err) {
      controllerRef.current = undefined
      if (err instanceof AbortError) {
        return
      }

      throw err
    }
  }, 300), [spotsFetcher])

  const handleInputChange = useEffectEvent(async (event: any, search) => {
    if (running.current) {
      return
    }
    running.current = true
    setStatus('loading')
    setError(undefined)

    const options: (SpotPartial | CustomItem)[] = []
    try {
      const response = await fetch(search)
      setStatus(undefined)

      if (response) {
        options.push(...response.member)
        if (response.totalItems > options.length) {
          options.push({
            type: typeMore,
            name: t('page.landing.spot_selector.option.more', {count: response.totalItems - options.length}),
          })
        }
      }
    } catch (err) {
      if (err instanceof Error) {
        setStatus('error')
        setError(t('page.landing.spot_selector.fetch.error'))
      } else {
        throw err
      }
    } finally {
      running.current = false
    }

    setOptions(options)
  })
  useEffect(() => {
    handleInputChange(undefined, '')
  }, [handleInputChange])

  const filterOptions = useCallback((options: (SpotPartial | CustomItem)[]): (SpotPartial | CustomItem)[] => {
    if (status === 'loading' || options.length > 0) {
      return options
    }

    return []
  }, [status])

  const isOptionEqualToValue = useCallback((option: SpotPartial | CustomItem, value: SpotPartial | CustomItem) => {
    if ('type' in option) {
      if ('type' in value) {
        return option.type === value.type
      }

      return false
    }
    if ('type' in value) {
      return false
    }

    return option['@id'] === value['@id']
  }, [])

  const handleChange = useEffectEvent((event: any, newValue: SpotPartial | CustomItem | undefined) => {
    if (!newValue) {
      return
    }

    if ('type' in newValue) {
      return
    }

    // delay call to `navigate` to let time to AutoComplete to close the portal
    queueMicrotask(() => navigate(getSpotUrl(newValue.slug)))
  })

  const renderInput = useCallback((params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      label={t('page.landing.spot_selector.title')}
      error={!!fetchError}
      helperText={fetchError ?? ''}
      sx={{
        [`.${inputBaseClasses.root}`]: {
          backgroundColor: 'common.white',
        },
      }}
      slotProps={{
        input: {
          ...params.InputProps,
          endAdornment: <>
            {status === 'loading' ? <CircularProgress color="inherit" size={20}/> : null}
            {status === 'error' ? <ErrorIcon color="error"/> : null}
            {params.InputProps.endAdornment}
          </>,
        },
      }}
    />
  ), [fetchError, status, t])

  const renderOptions = useCallback(({key, ...props}: HTMLAttributes<HTMLLIElement> & {
    key: any
  }, option: SpotPartial | CustomItem) => {
    if ('type' in option) {
      if (option.type === typeError) {
        return <li key={key} {...props}>
          <Alert severity="error" sx={{width: '100%'}}>
            {option.name}
          </Alert>
        </li>
      }

      return <li key={key} {...props}>
        <Typography variant="body2">
          {option.name}
        </Typography>
      </li>
    }

    return <li key={key} {...props}>
      <Typography variant="body1">
        {option.name}
      </Typography>
    </li>
  }, [])

  return <Autocomplete
    fullWidth

    getOptionLabel={(option: SpotPartial | CustomItem) => option.name ?? ''}
    getOptionDisabled={(option: SpotPartial | CustomItem) => 'type' in option}
    noOptionsText={t('page.landing.spot_selector.option.nodata')}

    disableClearable
    autoHighlight={true}
    autoComplete={true}
    forcePopupIcon={false}
    handleHomeEndKeys={false}
    clearOnBlur={false}

    filterOptions={filterOptions}
    isOptionEqualToValue={isOptionEqualToValue}
    options={options}
    onChange={handleChange}
    onInputChange={handleInputChange}
    renderInput={renderInput}
    renderOption={renderOptions}
  />
})
